import React, { useState, useEffect } from "react";
import {
  Box,
  MenuItem,
  Menu,
  useMediaQuery,
  IconButton,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import NotificationIconNav from "../../assets/icons/notification.png";
import styled from "styled-components";
import { useDispatch } from "react-redux";
import { handleLogout } from "../../redux/reducers/authReducer";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import axiosInstance from "../../helpers/AxiosInstance";
import Popover from "@mui/material/Popover";
import { ButtonsContainer, Notification } from "../utilities";
import Translation from "../utilities/Translation";
import LogoNav from "../../assets/images/logo-red1.png";
import { useLocation, useNavigate, Link } from "react-router-dom";

const NavbarContainer = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 70px;
  padding: 0 24px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(226, 232, 240, 0.8);
  z-index: 1000;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);

  @media (max-width: 768px) {
    height: 60px;
    padding: 0 12px;
  }
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  width: 420px;
  height: 400px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  overflow: hidden;
`;

const Line = styled.hr`
  border: 0;
  border-top: 1px solid #e2e8f0;
  margin: 0;
`;

const Button = styled(Link)`
  min-width: 162px;
  height: 44px;
  background: transparent;
  display: grid;
  place-items: center;
  text-align: center;
  font-weight: 700;
  font-family: Cairo;
  font-size: 15px;
  color: #084581;
  text-decoration: none;
  border-radius: 8px;
  transition: all 0.2s;
  &:hover {
    background: #f1f5f9;
  }
`;

const Logo = styled.img`
  height: 38px;
  object-fit: contain;
  transition: all 0.2s;
  @media (max-width: 768px) {
    height: 24px;
    max-width: 110px;
  }
`;

const IconsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const ProfileContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 30px;
  border: 1px solid #e2e8f0;
  background-color: #f8fafc;
  transition: all 0.2s ease-in-out;
  &:hover {
    border-color: #cbd5e1;
    background-color: #f1f5f9;
  }

  @media (max-width: 768px) {
    padding: 0;
    border: none;
    background-color: transparent;
    &:hover {
      background-color: transparent;
    }
  }
`;

const AvatarCircle = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: linear-gradient(135deg, #001c4c 0%, #084581 100%);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 13px;
  border: 2px solid #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  @media (max-width: 768px) {
    width: 34px;
    height: 34px;
    font-size: 12px;
  }
`;

const ClientType = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const NotificationIcon = styled(IconButton)`
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  transition: all 0.2s;
  &:hover {
    background-color: #f1f5f9;
  }
  @media (max-width: 768px) {
    width: 34px;
    height: 34px;
  }
`;

const NotificationBadge = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 8px;
  height: 8px;
  background-color: #ef4444;
  border-radius: 50%;
  display: ${(props) => (props.show ? "block" : "none")};
  @media (max-width: 768px) {
    top: 6px;
    right: 6px;
  }
`;

const MobileSearchOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #ffffff;
  z-index: 1010;
  display: flex;
  align-items: center;
  padding: 0 12px;
  gap: 8px;
`;

export default function Navbar({ sideOpen, RoleID }) {
  const { pathname } = useLocation();
  const { t } = useTranslation("layout");
  const { i18n } = useTranslation();
  const matches = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };
  const [anchor, setAnchor] = useState(null);
  const openNotification = Boolean(anchor);
  const handleNotificationClick = (event) => {
    setAnchor(event.currentTarget);
  };
  const [showSearch, setShowSearch] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [mobileSearchActive, setMobileSearchActive] = useState(false);
  const instance = axiosInstance();
  const { data } = useQuery(
    "notifications",
    async () => {
      const { data } = await instance.get("/GetActiveNotifications");
      return data;
    },
    {
      staleTime: 60000,
      cacheTime: 0,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    if (data) {
      setNotifications(data.notifications);
    }
  }, [data]);

  const { ClientName } = useSelector((state) => ({
    ClientName: state.Auth.AuthedUser?.ClientName || "User",
  }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (searchText.trim()) {
      navigate(`/shipment/${searchText}`);
      setSearchText("");
      setShowSearch(false);
      setMobileSearchActive(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const isRTL = i18n.language === "ar";

  return (
    <NavbarContainer>
      {/* Mobile Search Overlay */}
      {mobileSearchActive && (
        <MobileSearchOverlay>
          <IconButton 
            onClick={() => setMobileSearchActive(false)}
            sx={{ color: "#64748b" }}
          >
            {isRTL ? <ArrowForwardIcon /> : <ArrowBackIcon />}
          </IconButton>
          <form
            onSubmit={handleSubmit}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flex: 1,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                border: "1.5px solid #13cae9",
                borderRadius: "10px",
                overflow: "hidden",
                flex: 1,
                height: "40px",
                backgroundColor: "#ffffff",
              }}
            >
              <IconButton type="submit" sx={{ px: "10px", color: "#64748b" }}>
                <SearchIcon sx={{ fontSize: "20px" }} />
              </IconButton>
              <TextField
                size="small"
                autoFocus
                placeholder={t("Navbar.search_placeholder")}
                variant="standard"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                InputProps={{
                  disableUnderline: true,
                  sx: { ml: 1, flex: 1, fontSize: "14px" },
                }}
              />
            </div>
            <button
              type="submit"
              style={{
                background: "linear-gradient(135deg, #13cae9 0%, #0de3b8 100%)",
                color: "white",
                border: "none",
                padding: "0 16px",
                fontWeight: "bold",
                fontSize: "14px",
                cursor: "pointer",
                borderRadius: "10px",
                height: "40px",
              }}
            >
              {isRTL ? "بحث" : "Search"}
            </button>
          </form>
        </MobileSearchOverlay>
      )}

      <IconsContainer>
        <ProfileContainer onClick={handleMenuClick}>
          <AvatarCircle>
            {getInitials(ClientName)}
          </AvatarCircle>
          <Box sx={{ display: { xs: "none", md: "block" }, textAlign: isRTL ? "right" : "left" }}>
            <Box sx={{ fontSize: "14px", fontWeight: 700, color: "#0f172a" }}>
              {ClientName}
            </Box>
            <ClientType>
              {t("Navbar.prime_client")}
              <span style={{ fontSize: "8px", marginLeft: isRTL ? 0 : "4px", marginRight: isRTL ? "4px" : 0, opacity: 0.7 }}>▼</span>
            </ClientType>
          </Box>
        </ProfileContainer>

        <NotificationIcon onClick={handleNotificationClick}>
          <img src={NotificationIconNav} alt="notification" width={20} height={20} />
          <NotificationBadge show={notifications.length > 0} />
        </NotificationIcon>

        <IconButton
          sx={{
            display: { xs: "flex", md: "none" },
            width: 34,
            height: 34,
            border: "1px solid #e2e8f0",
            backgroundColor: "#f8fafc",
            borderRadius: "50%",
            justifyContent: "center",
            alignItems: "center",
          }}
          onClick={() => setMobileSearchActive(true)}
        >
          <SearchIcon sx={{ fontSize: "18px", color: "#64748b" }} />
        </IconButton>

        <Translation />
      </IconsContainer>
      
      <Menu anchorEl={anchorEl} open={openMenu} onClose={handleCloseMenu}>
        <MenuItem
          onClick={handleCloseMenu}
          component="a"
          href="/change-password"
        >
          {t("Navbar.change_password")}
        </MenuItem>
        <MenuItem onClick={() => dispatch(handleLogout())}>
          {t("Navbar.signout")}
        </MenuItem>
      </Menu>

      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          justifyContent: "center",
          flex: 1,
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: "400px",
          }}
        >
          <form
            onSubmit={handleSubmit}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                border: "1.5px solid #e2e8f0",
                borderRadius: "10px",
                overflow: "hidden",
                flex: 1,
                height: "40px",
                backgroundColor: "#f8fafc",
                transition: "all 0.2s ease-in-out",
              }}
              onFocusCapture={(e) => {
                e.currentTarget.style.borderColor = "#13cae9";
                e.currentTarget.style.backgroundColor = "#ffffff";
              }}
              onBlurCapture={(e) => {
                e.currentTarget.style.borderColor = "#e2e8f0";
                e.currentTarget.style.backgroundColor = "#f8fafc";
              }}
            >
              <IconButton type="submit" sx={{ px: "12px", color: "#64748b" }}>
                <SearchIcon sx={{ fontSize: "20px" }} />
              </IconButton>

              <TextField
                size="small"
                placeholder={t("Navbar.search_placeholder")}
                variant="standard"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                InputProps={{
                  disableUnderline: true,
                  sx: { ml: 1, flex: 1, fontSize: "14px" },
                }}
              />
            </div>

            <button
              type="submit"
              style={{
                background: "linear-gradient(135deg, #13cae9 0%, #0de3b8 100%)",
                color: "white",
                border: "none",
                padding: "0 20px",
                fontWeight: "bold",
                fontSize: "14px",
                cursor: "pointer",
                borderRadius: "10px",
                height: "40px",
                boxShadow: "0 4px 10px rgba(13, 227, 184, 0.2)",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = "translateY(-1px)";
                e.target.style.boxShadow = "0 6px 14px rgba(13, 227, 184, 0.3)";
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "0 4px 10px rgba(13, 227, 184, 0.2)";
              }}
            >
              {isRTL ? "بحث" : "Search"}
            </button>
          </form>
        </div>
      </Box>
      <Logo
        src={LogoNav}
        alt="Company Logo"
        style={{
          marginRight: isRTL ? "0" : "15px",
          marginLeft: isRTL ? "15px" : "0",
        }}
      />
      <Popover
        open={openNotification}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        onClick={() => {
          setAnchor(null);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{ fontFamily: "Cairo" }}
      >
        <Card>
          <div dir="rtl">
            {notifications.slice(0, 3).map((notification, index) => (
              <Notification
                key={index}
                setNotifications={setNotifications}
                notifications={notifications}
                notificationIndex={index}
                AWB={notification.AWB}
                Body={notification.Body}
                Status={notification.StatusName}
                Serial={notification.Serial}
              />
            ))}
            <Line />
            {notifications.length ? (
              <ButtonsContainer
                style={{ marginTop: "10px", marginBottom: "10px" }}
              >
                <Button to="/notifications" onClick={() => setAnchor(null)}>
                  {t("Navbar.all_notifications")}
                </Button>
              </ButtonsContainer>
            ) : (
              <ButtonsContainer
                style={{
                  marginTop: "10px",
                  marginBottom: "10px",
                  fontSize: "18px",
                }}
              >
                {t("Navbar.no_Notification")}
              </ButtonsContainer>
            )}
          </div>
        </Card>
      </Popover>
    </NavbarContainer>
  );
}

